

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MainLayout } from "../layout/MainLayout";
import FileUploadZone from "../uploads/FileUploadZone";
import UploadProgressDisplay from "../common/UploadProgressDisplay";
import apiClient from "../utils/apiClient";
import { 
  Minus as MinusIcon, 
  Check as CheckIcon, 
  Folder as FolderIcon, 
  Download, 
  Upload, 
  ArrowLeft,
  Database,
  Map,
  FileText,
  Layers,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader,
  ArrowRight,
  Server,
  Cloud
} from "lucide-react";

const GeodataSetup = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [county, setCounty] = useState("");
  const [checklist, setChecklist] = useState([0, 0, 0]); // DSM, DTM, CityGML
  const [loading, setLoading] = useState(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [token, setToken] = useState("");
  const [downloadStatus, setDownloadStatus] = useState("");

  // Fetch county and checklist data on component mount
  useEffect(() => {
    const fetchGeodataInfo = async () => {
      try {
        // Get auth token from local storage
        const authToken = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
        if (authToken) {
          setToken(authToken);
        }

        // Get project details
        const projectDetails = await apiClient.get(`/api/projects/${projectId}/get_project_details/`);
        
        if (projectDetails) {
          setCounty(projectDetails.city?.toUpperCase() || "Berlin");
          setProjectName(projectDetails.name || `Project ${projectId}`);

          // Get initial geodata status
          try {
            const geodataStatus = await apiClient.get(`/api/projects/${projectId}/geodata-status`, {
              useOperational: "new",
              headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
            });

            if (geodataStatus && geodataStatus.checklist) {
              setChecklist(geodataStatus.checklist);
            }
            if (geodataStatus && geodataStatus.status) {
              setDownloadStatus(geodataStatus.status);
            }
          } catch (error) {
            console.log("No existing geodata status found, using defaults");
          }
        }
      } catch (error) {
        console.error("Error fetching geodata info:", error);
        // Set defaults if API calls fail
        setCounty("Berlin");
        setProjectName(`Project ${projectId}`);
      }
    };

    fetchGeodataInfo();
  }, [projectId]);

  const handleDownloadFiles = async () => {
    try {
      setDownloadInProgress(true);
      setDownloadStatus("processing");

      console.log("Starting geodata download with token:", token ? "Token present" : "No token");
      console.log("Project ID:", projectId);
      console.log("County:", county);
      
      // Start the download process
      await apiClient.post(
        `/api/projects/${projectId}/acquire-geodata`,
        { county: county || "Berlin" },
        {
          useOperational: "new",
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      // Poll for status updates
      let completed = false;
      let pollAttempts = 0;
      const maxPollAttempts = 60; // 2 minutes max (60 * 2 seconds)

      while (!completed && pollAttempts < maxPollAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        pollAttempts++;

        try {
          const statusResponse = await apiClient.get(
            `/api/projects/${projectId}/geodata-status`,
            {
              useOperational: "new",
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            }
          );

          console.log("Status response:", statusResponse);
          setDownloadStatus(statusResponse.status);

          if (statusResponse.status === "done") {
            // Update the checklist to show all files are downloaded
            setChecklist(statusResponse.checklist || [1, 1, 1]);
            completed = true;
          } else if (statusResponse.status === "error") {
            console.error("Error downloading geodata:", statusResponse.errorMessage);
            setDownloadStatus("error");
            completed = true;
          }
        } catch (error) {
          console.error("Error polling status:", error);
          // Continue polling unless we've exceeded max attempts
        }
      }

      if (pollAttempts >= maxPollAttempts) {
        console.error("Download process timed out");
        setDownloadStatus("timeout");
      }

    } catch (error) {
      console.error("Error triggering geodata download:", error);
      setDownloadStatus("error");
    } finally {
      setDownloadInProgress(false);
    }
  };

  const handleFileUpload = async (files, fileType) => {
    if (files.length === 0) return;

    const file = files[0];

    try {
      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: `Uploading ${fileType} file...`,
      }));

      // Determine folder based on file type
      const subfolder = fileType === "GML" ? "GML" : fileType === "DSM" ? "DSM" : "DTM";
      const destPath = `projects/${projectId}/01_Input/GeospatialData/${subfolder}/${file.name}`;

      await apiClient.uploadFile("/api/files/upload-form", file, {
        destPath,
        overwrite: "true",
        fileType,
        projectId,
        useOperational: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Complete",
      }));

      // Update checklist
      const newChecklist = [...checklist];
      if (fileType === "DSM") newChecklist[0] = 1;
      if (fileType === "DTM") newChecklist[1] = 1;
      if (fileType === "GML") newChecklist[2] = 1;
      setChecklist(newChecklist);

      setUploadStatus("success");
      setTimeout(() => setUploadStatus(null), 2000);

    } catch (error) {
      console.error(`Error uploading ${fileType} file:`, error);
      setUploadProgress((prev) => ({
        ...prev,
        [file.name]: "Failed",
      }));
      setUploadStatus("error");
    }
  };

  const handleSubmitUploads = async () => {
    navigate(`/project-processing/${projectId}`);
  };

  const ChecklistItem = ({ icon: Icon, label, isComplete, color, description }) => (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
      <div className={`${color} p-4 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-lg">{label}</h3>
              <p className="text-white/80 text-sm">{description}</p>
            </div>
          </div>
          <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
            {isComplete ? (
              <CheckCircle className="w-5 h-5 text-white" />
            ) : (
              <MinusIcon className="w-5 h-5 text-white/60" />
            )}
          </div>
        </div>
        {/* Decorative Elements */}
        <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-full"></div>
        <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-white/10 rounded-full"></div>
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <span className={`text-sm font-semibold ${
            isComplete ? 'text-green-700' : 'text-slate-500'
          }`}>
            {isComplete ? 'Downloaded successfully' : 'Pending download'}
          </span>
          {isComplete && (
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Ready</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const UploadCard = ({ icon: Icon, title, description, fileType, accept, acceptedTypes, color }) => (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
      <div className={`${color} p-4 relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
        <div className="relative z-10">
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-bold text-white text-lg mb-1">{title}</h3>
          <p className="text-white/80 text-sm">{description}</p>
        </div>
        {/* Decorative Elements */}
        <div className="absolute -top-4 -right-4 w-16 h-16 bg-white/10 rounded-full"></div>
        <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-white/10 rounded-full"></div>
      </div>
      <div className="p-4">
        <FileUploadZone
          onFilesSelect={(files) => handleFileUpload(files, fileType)}
          accept={accept}
          acceptedTypes={acceptedTypes}
        />
      </div>
    </div>
  );

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl mb-6 shadow-lg">
              <Database className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-4">
              Geodata Acquisition
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Download and upload geospatial data for your project in <span className="font-bold text-blue-600">{county}</span>
            </p>
          </div>

          {/* Project Info Bar */}
          <div className="flex flex-wrap gap-4 mb-8">
            <button 
              onClick={() => navigate(`/project-processing/${projectId}`)}
              className="group bg-white hover:bg-slate-50 text-slate-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 shadow-md hover:shadow-lg border border-slate-200"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
              Back to Processing
            </button>
            
            <div className="flex items-center gap-3 bg-white px-6 py-3 rounded-xl border border-slate-200 shadow-md">
              <Server className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-slate-700">{projectName}</span>
            </div>
          </div>

          {/* Step 1: Automatic Download */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <Cloud className="w-6 h-6 text-white" />
                <h2 className="text-2xl font-bold text-white">Automatic Download</h2>
              </div>
              <p className="text-blue-100 mt-2">Download geospatial data for {county} automatically from our servers</p>
            </div>
            
            <div className="p-8">
              {/* Status Display */}
              {downloadStatus && (
                <div className={`mb-8 p-6 rounded-xl border-2 ${
                  downloadStatus === "done" ? "bg-green-50 border-green-200" :
                  downloadStatus === "processing" ? "bg-blue-50 border-blue-200" :
                  downloadStatus === "error" ? "bg-red-50 border-red-200" :
                  "bg-yellow-50 border-yellow-200"
                }`}>
                  <div className="flex items-center gap-4">
                    {downloadStatus === "done" && <CheckCircle className="w-6 h-6 text-green-600" />}
                    {downloadStatus === "processing" && <Loader className="w-6 h-6 text-blue-600 animate-spin" />}
                    {downloadStatus === "error" && <AlertCircle className="w-6 h-6 text-red-600" />}
                    <div>
                      <p className={`font-bold text-lg ${
                        downloadStatus === "done" ? "text-green-800" :
                        downloadStatus === "processing" ? "text-blue-800" :
                        downloadStatus === "error" ? "text-red-800" :
                        "text-yellow-800"
                      }`}>
                        {downloadStatus === "done" && "Download completed successfully!"}
                        {downloadStatus === "processing" && "Download in progress..."}
                        {downloadStatus === "error" && "Download failed. Please try again."}
                        {downloadStatus === "timeout" && "Download timed out. Please try again."}
                      </p>
                      <p className={`text-sm mt-1 ${
                        downloadStatus === "done" ? "text-green-600" :
                        downloadStatus === "processing" ? "text-blue-600" :
                        downloadStatus === "error" ? "text-red-600" :
                        "text-yellow-600"
                      }`}>
                        {downloadStatus === "processing" && "This may take up to 2 minutes. Please stay on this page."}
                        {downloadStatus === "done" && "All geospatial data files have been downloaded and are ready for processing."}
                        {downloadStatus === "error" && "There was an issue downloading the files. Check your connection and try again."}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-amber-600 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-amber-800 mb-2">Important Notice</h3>
                    <p className="text-amber-700">
                      The download process may take up to 2 minutes depending on file sizes. Please remain on this page during the download to ensure completion.
                    </p>
                  </div>
                </div>
              </div>

              {/* File Status Checklist */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <ChecklistItem 
                  icon={Layers} 
                  label="DSM" 
                  description="Digital Surface Model"
                  isComplete={checklist[0] === 1}
                  color="bg-gradient-to-br from-blue-500 to-cyan-500"
                />
                <ChecklistItem 
                  icon={Map} 
                  label="DTM" 
                  description="Digital Terrain Model"
                  isComplete={checklist[1] === 1}
                  color="bg-gradient-to-br from-green-500 to-emerald-500"
                />
                <ChecklistItem 
                  icon={FileText} 
                  label="CityGML" 
                  description="City Geography Markup"
                  isComplete={checklist[2] === 1}
                  color="bg-gradient-to-br from-purple-500 to-pink-500"
                />
              </div>

              {/* Download Button */}
              <div className="text-center">
                <button
                  onClick={handleDownloadFiles}
                  disabled={downloadInProgress}
                  className={`group inline-flex items-center gap-4 px-8 py-4 rounded-xl font-bold text-lg shadow-lg transition-all duration-300 ${
                    downloadInProgress
                      ? "bg-slate-400 text-white cursor-not-allowed"
                      : "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white hover:shadow-xl transform hover:scale-105"
                  }`}
                >
                  {downloadInProgress ? (
                    <>
                      <Loader className="w-6 h-6 animate-spin" />
                      Downloading Files...
                    </>
                  ) : (
                    <>
                      <Download className="w-6 h-6 group-hover:scale-110 transition-transform duration-200" />
                      Start Download
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Step 2: Manual Upload */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <Upload className="w-6 h-6 text-white" />
                <h2 className="text-2xl font-bold text-white">Manual Upload</h2>
              </div>
              <p className="text-indigo-100 mt-2">Upload your own geospatial data files if you prefer custom data</p>
            </div>
            
            <div className="p-8">
              <div className="grid md:grid-cols-3 gap-6">
                <UploadCard
                  icon={FileText}
                  title="CityGML"
                  description="Geography Markup Language"
                  fileType="GML"
                  accept=".gml"
                  acceptedTypes={["GML"]}
                  color="bg-gradient-to-br from-purple-500 to-pink-500"
                />
                <UploadCard
                  icon={Layers}
                  title="DSM"
                  description="Digital Surface Model"
                  fileType="DSM"
                  accept=".tif,.tiff"
                  acceptedTypes={["TIFF"]}
                  color="bg-gradient-to-br from-blue-500 to-cyan-500"
                />
                <UploadCard
                  icon={Map}
                  title="DTM"
                  description="Digital Terrain Model"
                  fileType="DTM"
                  accept=".tif,.tiff"
                  acceptedTypes={["TIFF"]}
                  color="bg-gradient-to-br from-green-500 to-emerald-500"
                />
              </div>

              {/* Upload Progress */}
              {Object.keys(uploadProgress).length > 0 && (
                <div className="mt-8">
                  <h3 className="font-semibold text-slate-800 mb-4">Upload Progress</h3>
                  <UploadProgressDisplay progress={uploadProgress} />
                </div>
              )}

              {/* Upload Status */}
              {uploadStatus && (
                <div className={`mt-8 p-6 rounded-xl border-2 ${
                  uploadStatus === "success"
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}>
                  <div className="flex items-center gap-4">
                    {uploadStatus === "success" ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    )}
                    <div>
                      <p className={`font-bold text-lg ${
                        uploadStatus === "success" ? "text-green-800" : "text-red-800"
                      }`}>
                        {uploadStatus === "success"
                          ? "Files uploaded successfully!"
                          : "Upload failed"}
                      </p>
                      <p className={`text-sm mt-1 ${
                        uploadStatus === "success" ? "text-green-600" : "text-red-600"
                      }`}>
                        {uploadStatus === "success"
                          ? "Your files have been uploaded and are ready for processing."
                          : "There was an error uploading your files. Please try again."}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Continue Button */}
          <div className="text-center">
            <button
              onClick={handleSubmitUploads}
              className="group inline-flex items-center gap-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-12 py-4 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              <span>Continue to Processing</span>
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-200" />
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default GeodataSetup;